import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Status } from './status'
import { BugTrackerTooltip, BugTracker } from '../bug-tracker'

const bugTrackerContainerCss = css`
  text-transform: none;
  margin-left: 5px;
`

const vulnerabilityStatusBadgeCss = css`
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  font-size: 12px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 20px;
  border-radius: 2px;
  color: ${theme.color.white};
  line-height: 1.4;
`
const statusNameToCss = {
  Confirmed: css`
    background-color: ${theme.color.yellow50};
  `,
  Suspicious: css`
    background-color: ${theme.color.red50};
  `,
  AutoRemediated: css`
    background-color: ${theme.color.neutral30};
    color: ${theme.color.neutral70};
  `,
  Remediated: css`
    background-color: ${theme.color.neutral30};
    color: ${theme.color.neutral70};
  `,
  Fixed: css`
    background-color: ${theme.color.neutral30};
    color: ${theme.color.neutral70};
  `,
  NotAProblem: css`
    background-color: ${theme.color.neutral30};
    color: ${theme.color.neutral70};
  `,
  Reported: css`
    background-color: ${theme.color.neutral100};
  `
}

export function VulnerabilityStatusBadge({
  status,
  pendingStatus,
  bugTrackers,
  organizationID,
  vulnerabilityID,
  showBugTracker,
  e2eTestId
}: {
  readonly status: Status
  readonly pendingStatus?: Status
  readonly bugTrackers?: BugTracker[]
  readonly organizationID?: string
  readonly vulnerabilityID?: string
  readonly showBugTracker: boolean
  readonly e2eTestId?: string
}) {
  const visibleStatus = pendingStatus || status
  if (showBugTracker) {
    if (!vulnerabilityID) {
      throw new Error(
        'vulnerabilityID prop is required if showBugTracker is true'
      )
    }
    if (!organizationID) {
      throw new Error(
        'organizationID prop is required if showBugTracker is true'
      )
    }
    if (!bugTrackers) {
      throw new Error('bugTrackers prop is required if showBugTracker is true')
    }
  }
  return (
    <div data-e2e={e2eTestId}>
      <FormattedMessage id={visibleStatus.i18nID}>
        {(text) => {
          return (
            <div
              className={cx(
                vulnerabilityStatusBadgeCss,
                statusNameToCss[visibleStatus.name],
                pendingStatus
                  ? css`
                      width: auto;
                      padding-left: 10px;
                      padding-right: 10px;
                    `
                  : css`
                      min-width: ${showBugTracker ? 105 : 95}px;
                      max-width: 150px;
                      padding-left: 10px;
                      padding-right: 10px;
                    `
              )}
            >
              <span
                className={css`
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {text}
              </span>
              {pendingStatus && (
                <FormattedMessage
                  id="title_pending"
                  children={(msg) => (
                    <strong
                      className={css`
                        margin-left: 5px;
                      `}
                    >
                      ({msg})
                    </strong>
                  )}
                />
              )}

              {showBugTracker &&
                vulnerabilityID &&
                organizationID &&
                bugTrackers && (
                  <span className={bugTrackerContainerCss}>
                    <BugTrackerTooltip
                      organizationID={organizationID}
                      vulnerabilityID={vulnerabilityID}
                      bugTrackers={bugTrackers}
                      iconColor={getBugTrackerIconColor(visibleStatus)}
                    />
                  </span>
                )}
            </div>
          )
        }}
      </FormattedMessage>
    </div>
  )
}

function getBugTrackerIconColor(status: Status) {
  if (
    status === Status.CONFIRMED ||
    status === Status.REPORTED ||
    status === Status.SUSPICIOUS
  ) {
    return 'white'
  }
  return 'neutral60'
}
